import type { OutreachEvent } from '@shared/types/api';

export type DemoVisit = {
  id: string;
  sessionId: string;
  firstAt: string;
  lastAt: string;
  classification: string;
  reason: string;
  pattern: string;
  patternReason: string;
  events: OutreachEvent[];
};

export function groupDemoVisits(events: OutreachEvent[]): DemoVisit[] {
  const ordered = [...new Map(events.map(event => [event.eventId, event])).values()]
    .sort((a, b) => a.eventKey.localeCompare(b.eventKey));
  const firstVisitBySession = new Map<string, string>();
  for (const event of ordered) {
    if (event.sessionId && event.visitId && !firstVisitBySession.has(event.sessionId)) firstVisitBySession.set(event.sessionId, event.visitId);
  }
  const groups = new Map<string, DemoVisit>();
  for (const event of ordered) {
    const id = event.visitId || firstVisitBySession.get(event.sessionId) || (event.sessionId ? `session:${event.sessionId}` : `uncorrelated:${event.eventId}`);
    let group = groups.get(id);
    if (!group) {
      group = { id, sessionId: event.sessionId, firstAt: event.occurredAt, lastAt: event.occurredAt,
        classification: 'UNVERIFIED', reason: '', pattern: '', patternReason: '', events: [] };
      groups.set(id, group);
    }
    group.events.push(event);
    group.lastAt = event.occurredAt;
  }
  const visits = [...groups.values()].sort((a, b) => a.firstAt.localeCompare(b.firstAt));
  const previousByFingerprint = new Map<string, DemoVisit>();
  for (const visit of visits) {
    const automated = visit.events.find(event => event.classification === 'LIKELY_AUTOMATED');
    const human = visit.events.find(event => event.classification === 'LIKELY_HUMAN');
    visit.classification = automated ? 'LIKELY_AUTOMATED' : human ? 'LIKELY_HUMAN' : 'UNVERIFIED';
    visit.reason = automated?.classificationReason || human?.classificationReason || 'No trusted interaction signal. Opens and visibility do not establish a person.';
    const source = visit.events.find(event => event.sourceIpHash && event.userAgent);
    const fingerprint = source ? `${source.sourceIpHash}|${source.userAgent}` : '';
    const previous = fingerprint ? previousByFingerprint.get(fingerprint) : undefined;
    const gap = previous ? (Date.parse(visit.firstAt) - Date.parse(previous.lastAt)) / 1000 : NaN;
    if (previous && gap >= 0 && gap <= 300 && visit.classification !== 'LIKELY_HUMAN') {
      visit.pattern = 'Repeated initialization / possible scanning';
      visit.patternReason = `Same IP hash and user agent as an earlier loaded visit, ${Math.round(gap)} seconds later, without trusted interaction. Could also be a reload; this does not prove scanning or a return by a person.`;
    } else if (previous && gap >= 1800 && visit.classification === 'LIKELY_HUMAN' && previous.classification === 'LIKELY_HUMAN') {
      visit.pattern = 'Likely return visit';
      visit.patternReason = 'Matching IP hash and user agent with interaction signals in visits at least 30 minutes apart. Shared networks and browser automation can still mimic this; recipient identity is not verified.';
    } else {
      visit.pattern = visit.events.some(event => event.visitId) ? 'Distinct page visit' : 'Session exchange / uncorrelated access';
      visit.patternReason = 'A separate page render or session exchange is not itself evidence of a new person or genuine return visit.';
    }
    if (fingerprint) previousByFingerprint.set(fingerprint, visit);
  }
  return visits.reverse();
}

export function csvCell(input: unknown): string {
  let text = input === undefined || input === null ? '' : String(input);
  if (/^[\t\r\n ]*[=+\-@]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
}

export function eventCsvRows(events: OutreachEvent[], identity: unknown[]): string[] {
  return groupDemoVisits(events).flatMap(visit => visit.events.map(event => [
    ...identity, event.eventId, event.occurredAt, event.eventType, event.sessionId, event.visitId,
    event.clientType, event.userAgent, event.sourceIpHash, event.ipHashSource, event.requestId,
    event.classification, event.classificationReason, event.classificationVersion,
    visit.classification, visit.reason, visit.pattern, visit.patternReason,
    event.details?.assetId, event.details?.visibilityState, event.details?.trustedInteraction,
    event.details?.webdriver, event.details?.legacyUncorrelated,
  ].map(csvCell).join(',')));
}

export const EVENT_CSV_HEADERS = ['Invitation ID', 'Company', 'Recipient', 'Email', 'Event ID', 'Timestamp UTC',
  'Event Type', 'Session ID', 'Visit ID', 'Client Type', 'User Agent', 'IP Hash', 'IP Hash Source', 'Request ID',
  'Event Classification', 'Event Classification Reason', 'Classification Version', 'Visit Classification',
  'Visit Classification Reason', 'Visit Pattern', 'Visit Pattern Reason', 'Asset ID', 'Visibility',
  'Trusted Interaction (client reported)', 'WebDriver (client reported)', 'Legacy Uncorrelated'];
